import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, Mail, Loader2, ShieldAlert, ArrowLeft, Play, Send, CheckCircle2, XCircle, Clock } from "lucide-react";

/**
 * /admin/legacy-migration — one-shot admin tool to bulk-import legacy
 * customers from the owner's old Stripe account, filter out cancelled +
 * disputed rows, mint invite tokens, then blast welcome emails via Resend.
 */
export default function AdminLegacyMigration() {
  const { user } = useAuth();
  const [stripeKey, setStripeKey] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendStats, setSendStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const refreshStats = async () => {
    try {
      const r = await api.get("/admin/legacy-migration/stats");
      setDashboardStats(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => { refreshStats(); }, []);

  if (user && !user.is_admin) return <Navigate to="/" replace />;

  const runImport = async () => {
    if (!stripeKey.trim()) {
      toast.error("Paste your old Stripe secret key first");
      return;
    }
    if (!dryRun && !window.confirm("Run FOR REAL? This creates invite rows for every eligible legacy customer.")) return;
    setImporting(true);
    setImportStats(null);
    try {
      const r = await api.post("/admin/legacy-migration/import-from-stripe", {
        stripe_secret_key: stripeKey.trim(),
        dry_run: dryRun,
      }, { timeout: 600000 });
      setImportStats(r.data);
      await refreshStats();
      toast.success(dryRun ? "Dry-run complete — check the numbers" : "Import complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const sendInvites = async () => {
    if (!window.confirm(`Send welcome emails to ${dashboardStats?.pending || "?"} legacy customers?`)) return;
    setSending(true);
    setSendStats(null);
    try {
      const r = await api.post("/admin/legacy-migration/send-invites", { dry_run: false, limit: 1000 });
      setSendStats(r.data);
      await refreshStats();
      toast.success(`${r.data?.sent || 0} invites sent`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Link to="/dashboard" className="mono text-[10px] tracking-widest uppercase text-cyan/80 hover:text-cyan inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> Back to cockpit
        </Link>

        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// ADMIN TOOL</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Legacy Customer Migration</h1>
        <p className="text-white/60 mt-2 max-w-2xl">
          Bulk-import customers from your old Stripe account. Cancelled subscriptions and anyone who filed a dispute are automatically excluded. Eligible customers get a Founding Member badge + 12 months of PJP Digital ID free.
        </p>

        {/* Dashboard stats */}
        {dashboardStats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="legacy-stats">
            <StatCard label="Pending" value={dashboardStats.pending} icon={Clock} tone="cyan" />
            <StatCard label="Invited" value={dashboardStats.invited} icon={Send} tone="cyan" />
            <StatCard label="Claimed" value={dashboardStats.claimed} icon={CheckCircle2} tone="avail" />
            <StatCard label="Total" value={dashboardStats.total} icon={Users} tone="white" />
          </div>
        )}

        {/* STEP 1 — Import from Stripe */}
        <section className="mt-8 rounded-lg border border-white/10 bg-white/[0.02] p-6" data-testid="legacy-import-section">
          <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">STEP 1 — IMPORT</div>
          <h2 className="font-display text-xl font-semibold mt-0.5">Pull customers from your old Stripe</h2>
          <p className="text-white/60 text-sm mt-1">Paste the secret key from the Stripe account your legacy customers pay into. Read-only usage (Customer/Subscription/Dispute list) — never stored on our side.</p>

          <div className="mt-4 rounded border border-yellow-500/40 bg-yellow-500/[0.05] p-3 flex items-start gap-3">
            <ShieldAlert className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-yellow-200">Use a restricted-permissions key if you can — Customer/Subscription/Dispute read access is all we need. Rotate the key after this migration is done.</div>
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <label className="mono text-[10px] tracking-widest uppercase text-white/60">Old Stripe secret key</label>
              <div className="mt-1 flex gap-2">
                <input
                  type={showKey ? "text" : "password"}
                  value={stripeKey}
                  onChange={(e) => setStripeKey(e.target.value)}
                  placeholder="sk_live_… or sk_test_…"
                  data-testid="input-old-stripe-key"
                  className="flex-1 bg-black/40 border border-white/15 rounded px-3 py-2 text-white font-mono text-sm outline-none focus:border-cyan/50"
                />
                <button type="button" onClick={() => setShowKey(!showKey)} className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/15 text-white/60 hover:text-white">
                  {showKey ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer" data-testid="toggle-dry-run">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="h-4 w-4 accent-cyan" />
              Dry run first (recommended — counts eligibility but doesn't create rows)
            </label>
          </div>

          <button
            onClick={runImport}
            disabled={importing || !stripeKey.trim()}
            data-testid="btn-run-import"
            className="mt-4 mono text-[11px] tracking-widest uppercase px-4 py-2.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {importing ? "Scanning your Stripe…" : dryRun ? "Run Dry-Run" : "Import For Real"}
          </button>

          {importStats && (
            <div className="mt-5 rounded border border-cyan/40 bg-cyan/[0.06] p-4 text-sm" data-testid="import-result">
              <div className="mono text-[10px] tracking-widest uppercase text-cyan mb-2">{dryRun ? "Dry-run" : "Import"} results</div>
              <ul className="space-y-1 text-white/85">
                <li>Customers scanned: <span className="mono text-cyan">{importStats.customers_scanned}</span></li>
                <li>✅ Eligible for founding perk: <span className="mono text-avail">{importStats.eligible}</span></li>
                {!dryRun && <li>Inserted (new invite rows): <span className="mono text-avail">{importStats.inserted}</span></li>}
                <li>❌ Excluded — cancelled subs only: <span className="mono text-white/50">{importStats.excluded_cancelled_only}</span></li>
                <li>❌ Excluded — disputes filed: <span className="mono text-red-400">{importStats.excluded_disputed}</span></li>
                <li>❌ Excluded — no email: <span className="mono text-white/50">{importStats.excluded_no_email}</span></li>
                <li>ℹ️ Already on PJP: <span className="mono text-white/50">{importStats.already_on_pjp}</span></li>
                <li>ℹ️ Already invited: <span className="mono text-white/50">{importStats.already_invited}</span></li>
              </ul>
              {importStats.errors?.length > 0 && (
                <details className="mt-2 text-xs text-red-300"><summary>Errors ({importStats.errors.length})</summary><pre className="mt-1 text-[11px] whitespace-pre-wrap">{importStats.errors.join("\n")}</pre></details>
              )}
            </div>
          )}
        </section>

        {/* STEP 2 — Send invites */}
        <section className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-6" data-testid="legacy-send-section">
          <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">STEP 2 — INVITE</div>
          <h2 className="font-display text-xl font-semibold mt-0.5">Blast welcome emails</h2>
          <p className="text-white/60 text-sm mt-1">Each pending customer receives a personal magic-link from Jeff. Clicking it lets them set a password and claim their Founding Member perk in one screen. Handled by Resend if configured; otherwise queued to <code className="mono text-cyan">db.pending_emails</code>.</p>

          <button
            onClick={sendInvites}
            disabled={sending || !dashboardStats?.pending}
            data-testid="btn-send-invites"
            className="mt-4 mono text-[11px] tracking-widest uppercase px-4 py-2.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
            {sending ? "Sending…" : `Send to ${dashboardStats?.pending || 0} Pending`}
          </button>

          {sendStats && (
            <div className="mt-4 text-sm text-white/85" data-testid="send-result">
              <div>✅ Sent: <span className="mono text-avail">{sendStats.sent}</span></div>
              {sendStats.failed > 0 && <div>⚠️ Failed: <span className="mono text-red-400">{sendStats.failed}</span></div>}
            </div>
          )}
        </section>

        <div className="mt-8 text-xs text-white/50 space-y-2">
          <div><strong className="text-white/70">Note on rate limits:</strong> Stripe API tolerates a few requests/sec — the pull may take several minutes for large customer lists. Leave the page open.</div>
          <div><strong className="text-white/70">Note on Resend:</strong> if RESEND_API_KEY isn't set on production, invites are queued to the outbox instead of being delivered. Set it in the Deploy → Secrets tab, then redeploy.</div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const toneClasses = {
    cyan: "border-cyan/30 bg-cyan/[0.06] text-cyan",
    avail: "border-avail/30 bg-avail/[0.06] text-avail",
    white: "border-white/15 bg-white/[0.02] text-white",
  }[tone] || "border-white/10 bg-white/[0.02] text-white";
  return (
    <div className={`rounded-lg border p-3 ${toneClasses}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3 w-3" />
        <div className="mono text-[10px] tracking-widest uppercase opacity-70">{label}</div>
      </div>
      <div className="mono text-2xl font-bold">{value ?? 0}</div>
    </div>
  );
}
